import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis } from 'recharts';

import { ReactComponent as NavigateArrow } from '../../../../assets/icons/dashboard/navigate-arrow.svg';
import { chartTimePeriod } from '../../../../config/enum';
import currentTimeZone from '../../../../config/timeZone';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import { formatMinutesToHoursAndMinutes } from '../../../../shared-functions/DataTime';
import dashboard from '../../../../utils/api/partner/dashboard';

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

/**
 * Device duration - idle and in-use comparison chart
 * @returns {any} The result
 */
export default function DeviceDurationChart() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const activeRangeReference = React.useRef<
    'last24hours' | 'last7days' | 'lastMonth' | 'last12months'
  >('last24hours');

  /**
   * Fetch power consumed data based on time range
   * @param {any} range - The range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    activeRangeReference.current = range;
    try {
      setIsLoading(true);
      const response = await dashboard.getDeviceActiveIdleReport({
        timePeriod: range,
        timeZone: currentTimeZone,
      });

      // ❌ If user already switched to another range, ignore this response
      if (activeRangeReference.current !== range) return;

      // ✅ Otherwise, update the chart with the right data
      if (range === 'last12months') {
        setBarGraphData(fillMissingMonthsGeneric(response.data));
      } else if (range === 'lastMonth') {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 30));
      } else if (range === 'last7days') {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 7));
      } else if (range === 'last24hours') {
        setBarGraphData(fillMissingLast24HrsGeneric(response.data));
      }
      setIsLoading(false);
    } catch (error) {
      console.log(error);

      // only stop loading if this request is still the active one
      if (activeRangeReference.current === range) {
        setIsLoading(false);
      }
    }
  };

  /**
   * Function to show custom tooltip
   * @param {any} param0 - The param0
   * @param {any} param0.active - The active prop
   * @param {any} param0.payload - The payload prop
   * @returns {any} The result
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0];

      return (
        <div className="device-duration-custom-tooltip-partner">
          <p className="label">{data?.payload?.label}</p>
          <div className="tooltip-values">
            <p style={{ color: '#2AA700' }}>
              {t('inUse')}:
              {formatMinutesToHoursAndMinutes(data?.payload?.totalConsumptionInMinutes || 0)}
            </p>
            {/* <p style={{ color: '#0099D6' }}>Idle: {data?.payload?.idleHours}</p> */}
          </div>
        </div>
      );
    }

    return null;
  }

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, []);

  return (
    <div className="dashboard-device-duration-chart-wrap-partner">
      <div className="dashboard-device-duration-chart-header">
        <div className="header-title">
          <h6>{t('durationDevice')}</h6>
          <span
            className="header-arrow"
            role="presentation"
            onClick={() => navigate('/ev-admin/operations/chargers')}>
            <NavigateArrow />
          </span>
        </div>
        <CustomToggleButton
          labels={chartTimePeriod.map((duration) => t(duration.label))}
          onToggle={(index, label) => {
            const selectedDuration = chartTimePeriod.find(
              (duration) => t(duration.label) === label,
            );
            fetchPowerConsumedReport(selectedDuration?.value || 'last24hours');
            setIsLoading(true);
          }}
          defaultActiveIndex={0}
          type="chart"
        />
      </div>
      {isLoading ? (
        <center className="device-duration-loader">
          <BatteryLoader />
        </center>
      ) : (
        <>
          <div className="dashboard-device-duration-chart-container">
            <ResponsiveContainer width="100%" height={275}>
              <BarChart data={barGraphData} barCategoryGap={25}>
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  fontSize={12}
                  stroke="#535353"
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                {/* <YAxis
                      axisLine={false}
                      tickLine={false}
                      domain={['dataMin - 1', 'dataMax + 1']}
                    /> */}
                <Bar dataKey="totalConsumptionInMinutes" stackId="a" barSize={26} fill="#2AA700" />
                {/* <Bar dataKey="idleHours" stackId="a" barSize={26} fill="#0099D6" /> */}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="device-duration-chart-label">
            <div className="each-label">
              <div className="each-label-color" style={{ backgroundColor: '#2AA700' }} />
              <p>{t('inUse')}</p>
            </div>
            {/* <div className="each-label">
              <div className="each-label-color" style={{ backgroundColor: '#0099D6' }} />
              <p>Idle</p>
            </div> */}
          </div>
        </>
      )}
    </div>
  );
}
